import React, { useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  EmptyBox,
  Heading,
  Icon,
  Input,
  Link,
  Progress,
  SimplePopover,
  SimpleTable,
  Stack,
  Text
} from '@/components/lib'
import {
  FaCheckCircle,
  FaDownload,
  FaEnvelope,
  FaPhone,
  FaTimesCircle,
  FaWhatsapp,
  FaClock,
  FaList
} from 'react-icons/fa'
import api from '../../../api'
import utils, { formatKycaidAddresses } from '../../../utils/utils'
import useStore from '../../../store'

export default function SignedConsentsTab ({ trial }) {
  const { user } = useStore()

  const [consents, setConsents] = useState([])
  const [totalConsents, setTotalConsents] = useState(0)
  const [loadingConsents, setLoadingConsents] = useState(false)
  const [consentsQuery, setConsentsQuery] = useState('')
  const [consentsPage, setConsentsPage] = useState(1)

  useEffect(() => {
    if (!trial?._id || !utils.canReadTrialConsents(user, trial)) return
    setLoadingConsents(true)
    api.trials.listConsentsForTrial(
      trial._id,
      { page: consentsPage, query: consentsQuery },
      (data) => {
        setLoadingConsents(false)
        setConsents(data.consents)
        setTotalConsents(data.total)
      }
    )
  }, [trial?._id, user, trial, consentsPage, consentsQuery])

  function renderIdentityVerificationCell (consent) {
    const iv = consent.identityVerificationObject
    if (!iv) return '—'
    const status = iv.status || '—'
    const applicant = (iv.kycaidData || {}).applicant || {}
    const name =
      [applicant.first_name, applicant.last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || '—'
    const address = formatKycaidAddresses(applicant.addresses)
    const gender = (applicant.gender || '—').toUpperCase()
    const docTypes =
      (applicant.documents || [])
        .map((d) => d.type)
        .filter(Boolean)
        .join(', ') || '—'
    const declineReasons = (iv.kycaidDeclineReasons || []).length
      ? (iv.kycaidDeclineReasons || []).join(', ')
      : 'None'
    const badgeColor =
      status === 'verified'
        ? 'green'
        : status === 'failed'
          ? 'red'
          : status === 'submitted'
            ? 'orange'
            : 'gray'
    const badgeIcon =
      status === 'verified'
        ? (
          <Icon as={FaCheckCircle} />
          )
        : status === 'failed'
          ? (
            <Icon as={FaTimesCircle} />
            )
          : status === 'submitted'
            ? (
              <Icon as={FaClock} />
              )
            : null
    const statusLabel =
      status === 'verified'
        ? 'Verified'
        : status === 'failed'
          ? 'Failed'
          : status === 'submitted'
            ? 'Pending decision'
            : status
    return (
      <SimplePopover
        key={consent._id}
        trigger={
          <Badge colorPalette={badgeColor} cursor='pointer' size='sm'>
            {badgeIcon} {status === 'submitted' ? 'Pending' : name}
          </Badge>
        }
        header='Identity verification'
        body={
          <Stack gap={2} fontSize='sm'>
            <Box>
              <Text fontWeight='bold'>Status</Text>
              <Text>{statusLabel}</Text>
            </Box>
            <Box>
              <Text fontWeight='bold'>Name</Text>
              <Text>{name}</Text>
            </Box>
            <Box>
              <Text fontWeight='bold'>Address</Text>
              <Text whiteSpace='pre-line'>{address}</Text>
            </Box>
            <Box>
              <Text fontWeight='bold'>Gender</Text>
              <Text>{gender}</Text>
            </Box>
            <Box>
              <Text fontWeight='bold'>Documents used</Text>
              <Text>{docTypes}</Text>
            </Box>
            <Box>
              <Text fontWeight='bold'>Decline reasons</Text>
              <Text>{declineReasons}</Text>
            </Box>
          </Stack>
        }
      />
    )
  }

  return (
    <Box>
      <Heading as='h3' size='md'>
        Signed consent forms
      </Heading>
      <Text mb={3}>
        Find below the currently-signed consent forms submitted for this trial.
      </Text>

      <Box display='flex' justifyContent='space-between' mb={3}>
        <Input
          size='sm'
          placeholder='Filter consents...'
          value={consentsQuery}
          onChange={(e) => {
            setConsentsQuery(e.target.value)
            setConsentsPage(1)
          }}
          maxW='150px'
        />

        <Stack spacing={2} direction='row' alignItems='center'>
          {consentsPage > 1 && (
            <Button size='sm' onClick={() => setConsentsPage(consentsPage - 1)}>
              Previous
            </Button>
          )}
          <Text fontSize='sm'>Page {consentsPage}</Text>
          {consents?.length > 0 && (
            <Button size='sm' onClick={() => setConsentsPage(consentsPage + 1)}>
              Next
            </Button>
          )}
        </Stack>
      </Box>

      {loadingConsents && (
        <Progress isIndeterminate colorPalette='primary' size='sm' />
      )}

      {consents?.length === 0 && totalConsents === 0 && (
        <EmptyBox
          icon='signature'
          title='No consents yet'
          description='eConsent documents will show here once they are submitted.'
        />
      )}

      {consents?.length > 0 && (
        <SimpleTable
          asCard
          headers={[
            'Consented at',
            'Consent key',
            'Name',
            'Contact',
            'Participant',
            'Identity verification',
            ''
          ]}
          rows={consents?.map((consent) => {
            const base = [
              utils.formatDateTime(consent.createdAt),
              <Stack
                key={consent._id}
                gap={1}
                direction='row'
                alignItems='center'
              >
                {consent.consentDocumentFormKey
                  ? (
                    <Text>{consent.consentDocumentFormKey}</Text>
                    )
                  : (
                    <Badge colorPalette='gray' size='sm'>
                      Unknown
                    </Badge>
                    )}
              </Stack>,
              consent.fullName,
              <Stack key={consent._id} gap={1}>
                {consent.phone && (
                  <Stack gap={1} direction='row' alignItems='center'>
                    <Icon as={FaPhone} color='fg.subtle' />
                    <Text>{consent.phone}</Text>
                  </Stack>
                )}
                {consent.whatsapp && (
                  <Stack gap={1} direction='row' alignItems='center'>
                    <Icon as={FaWhatsapp} color='fg.subtle' />
                    <Text>{consent.whatsapp}</Text>
                  </Stack>
                )}
                {consent.email && (
                  <Stack gap={1} direction='row' alignItems='center'>
                    <Icon as={FaEnvelope} color='fg.subtle' />
                    <Text>{consent.email}</Text>
                  </Stack>
                )}
              </Stack>,
              consent.participant
                ? (
                  <Link
                    key={consent._id}
                    to={`/trials/${trial?._id}/participants/${consent.participant._id}`}
                  >
                    {consent.participant.id ?? consent.participant._id}
                  </Link>
                  )
                : (
                    '—'
                  )
            ]
            base.push(renderIdentityVerificationCell(consent))
            base.push(
              <Stack key={consent._id} direction='row' gap={2} alignItems='center'>
                {consent.fileUrl && (
                  <Button
                    as='a'
                    target='_blank'
                    href={consent.fileUrl}
                    size='xs'
                    colorPalette='primary'
                    variant='outline'
                  >
                    <Icon as={FaDownload} mr={2} /> Download
                  </Button>
                )}
                {consent.sessionEvents?.length > 0 && (
                  <SimplePopover
                    trigger={
                      <Button size='xs' variant='outline'>
                        <Icon as={FaList} mr={2} /> Events
                      </Button>
                    }
                    header='Session events'
                    body={
                      <Stack gap={2} fontSize='xs' maxH='300px' overflowY='auto'>
                        {consent.sessionEvents.map((event, i) => (
                          <Box key={i} borderBottomWidth='1px' borderColor='border.subtle' pb={1}>
                            <Text fontWeight='bold'>{event.type}</Text>
                            <Text color='fg.muted'>{utils.formatDateTime(event.createdAt, { withSeconds: true })}</Text>
                            {event.clientIp && <Text color='fg.subtle'>IP: {event.clientIp}</Text>}
                            {event.clientUserAgent && <Text color='fg.subtle' wordBreak='break-all'>UA: {event.clientUserAgent}</Text>}
                            {event.data && Object.keys(event.data).length > 0 && (
                              <Text color='fg.subtle' whiteSpace='pre-wrap'>
                                {Object.entries(event.data).map(([k, v]) => `${k}: ${v}`).join('\n')}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    }
                  />
                )}
              </Stack>
            )
            return base
          })}
        />
      )}
    </Box>
  )
}
